/**
 * Utility functions for document handling
 */

import { Dispatch } from "@reduxjs/toolkit";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import { DraftData, FileData } from "../types";
import { DOCUMENT_CONFIGS, REDUX_ACTION_MAP } from "../documentConfig";
import {
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setBankDoc,
  setBalance,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
} from "../../../features/merchant/doc";

/**
 * Hydrates all document fields into Redux from draft data
 * Uses unwrapped payload pattern
 */
export const hydrateDocumentsToRedux = (
  dispatch: Dispatch,
  draftData: DraftData
) => {
  dispatch(setProofDoc(draftData.proofOfAddress));
  dispatch(setBusinessDoc(draftData.businessProfile));
  dispatch(setShareDoc(draftData.shareHoldersDirectorsRegisters));
  dispatch(setBankDoc(draftData.bankStatement));
  dispatch(setBalance(draftData.balanceSheetPAndLStatement));
  dispatch(
    setCertificateOfIncorporationDoc(draftData.certificateOfIncorporation)
  );
  dispatch(
    setSupportingDocumentsWithSuppliersDoc(
      draftData.supportingDocumentsWithSuppliers
    )
  );
};

/**
 * Gets the Redux action for a given file type
 * Uses unwrapped payload pattern - direct file array passing
 */
export const getDocumentAction = (fileType: FileEnum, files: FileData[]) => {
  const actionCreator =
    REDUX_ACTION_MAP[fileType as keyof typeof REDUX_ACTION_MAP];

  if (!actionCreator) {
    console.warn(`No action creator found for file type: ${fileType}`);
    return null;
  }

  // All document actions now use unwrapped payloads
  return actionCreator(files);
};

/**
 * Validates all required documents are uploaded
 * Returns array of missing document labels
 */
export const validateDocuments = (documents: {
  business: FileData[];
  share: FileData[];
  bankDoc: FileData[];
  proof: FileData[];
  balance: FileData[];
  certificateOfIncorporation: FileData[];
  supportingDocumentsWithSuppliers: FileData[];
}): string[] => {
  const missingFiles: string[] = [];

  if (documents.business.length === 0) {
    missingFiles.push("company/business Profile");
  }
  if (documents.share.length === 0) {
    missingFiles.push("shareholder and directors registers file");
  }
  if (documents.bankDoc.length === 0) {
    missingFiles.push("proof of bank account details");
  }
  if (documents.proof.length === 0) {
    missingFiles.push("proof of residential address");
  }
  if (documents.balance.length === 0) {
    missingFiles.push("financial documents");
  }
  if (documents.certificateOfIncorporation.length === 0) {
    missingFiles.push("certificate of incorporation");
  }
  if (documents.supportingDocumentsWithSuppliers.length === 0) {
    missingFiles.push("supporting documents with suppliers");
  }

  return missingFiles;
};

/**
 * Extracts file paths from file data array
 */
export const extractFilePaths = (files: FileData[]): string[] => {
  return (files || []).map((f) => f.path);
};

/**
 * Gets existing files for a given file type from Redux state
 */
export const getExistingFilesForType = (
  fileType: FileEnum,
  documents: {
    certificateOfIncorporation: FileData[];
    proof: FileData[];
    share: FileData[];
    bankDoc: FileData[];
    business: FileData[];
    balance: FileData[];
    supportingDocumentsWithSuppliers: FileData[];
  }
): FileData[] => {
  switch (fileType) {
    case FileEnum.CERTIFICATE_OF_INCORPORATION:
      return documents.certificateOfIncorporation;
    case FileEnum.PROOF_OF_ADDRESS:
      return documents.proof;
    case FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS:
      return documents.share;
    case FileEnum.BANK_STATEMENT:
      return documents.bankDoc;
    case FileEnum.BUSINESS_PROFILE:
      return documents.business;
    case FileEnum.BALANCE_SHEET:
      return documents.balance;
    case FileEnum.SUPPORTING_DOCUMENTS_WITH_SUPPLIERS:
      return documents.supportingDocumentsWithSuppliers;
    default:
      return [];
  }
};
